/**
 * Module definitions based on database structure
 * Consolidated module system for permissions and navigation
 */

export interface ModuleDefinition {
  id: number;
  name: string;
  displayName: string;
  description: string;
  moduleKey: string;
  isActive: boolean;
  sortOrder: number;
  category: 'core' | 'service' | 'detailed';
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  // Core system modules
  { 
    id: 1, 
    name: 'Organizations', 
    displayName: 'Organizations', 
    description: 'Manage organizations and their settings', 
    moduleKey: 'ORG', 
    isActive: true, 
    sortOrder: 1,
    category: 'core'
  },
  { 
    id: 2, 
    name: 'Users', 
    displayName: 'Users', 
    description: 'Manage roles and permissions', 
    moduleKey: 'USERS', 
    isActive: true, 
    sortOrder: 2,
    category: 'core'
  },
  { 
    id: 3, 
    name: 'Tracking', 
    displayName: 'Tracking', 
    description: 'Track your orders', 
    moduleKey: 'TRACK', 
    isActive: true, 
    sortOrder: 0,
    category: 'core'
  },
  { 
    id: 4, 
    name: 'Dashboard', 
    displayName: 'Dashboard', 
    description: 'Access to main dashboard', 
    moduleKey: 'DASHBOARD', 
    isActive: true, 
    sortOrder: 0,
    category: 'core'
  },
  
  // Service-level modules
  { 
    id: 166, 
    name: 'User Management', 
    displayName: 'User Management Service', 
    description: 'Core user management functionality', 
    moduleKey: 'user_management', 
    isActive: true, 
    sortOrder: 1,
    category: 'service'
  },
  { 
    id: 167, 
    name: 'Purchase Management', 
    displayName: 'Purchase Management Service', 
    description: 'Purchase orders and supplier operations', 
    moduleKey: 'purchase_management', 
    isActive: true, 
    sortOrder: 2,
    category: 'service'
  },
  { 
    id: 168, 
    name: 'Sales Management', 
    displayName: 'Sales Management Service', 
    description: 'Sales orders and customer operations', 
    moduleKey: 'sales_management', 
    isActive: true, 
    sortOrder: 3,
    category: 'service'
  },
  { 
    id: 169, 
    name: 'Vendor Management', 
    displayName: 'Vendor Management Service', 
    description: 'Supplier and vendor relationship management', 
    moduleKey: 'vendor_management', 
    isActive: true, 
    sortOrder: 4,
    category: 'service'
  },
  { 
    id: 170, 
    name: 'Inventory Management', 
    displayName: 'Inventory Management Service', 
    description: 'Pure stock and warehouse operations', 
    moduleKey: 'inventory_management', 
    isActive: true, 
    sortOrder: 5,
    category: 'service'
  },
  { 
    id: 171, 
    name: 'Catalog Management', 
    displayName: 'Catalog Management Service', 
    description: 'Product catalog operations', 
    moduleKey: 'catalog_management', 
    isActive: true, 
    sortOrder: 6,
    category: 'service'
  },
  { 
    id: 172, 
    name: 'Task Management', 
    displayName: 'Task Management Service', 
    description: 'Task and workflow management', 
    moduleKey: 'task_management', 
    isActive: true, 
    sortOrder: 7,
    category: 'service'
  },
  { 
    id: 173, 
    name: 'Reporting', 
    displayName: 'Reporting Service', 
    description: 'Reports and analytics', 
    moduleKey: 'reporting', 
    isActive: true, 
    sortOrder: 8,
    category: 'service'
  },
  { 
    id: 174, 
    name: 'Customer Interfaces', 
    displayName: 'Customer Interfaces Service', 
    description: 'Customer-facing features', 
    moduleKey: 'customer_interfaces', 
    isActive: true, 
    sortOrder: 9,
    category: 'service'
  },
  
  // Detailed modules
  { 
    id: 175, 
    name: 'Users', 
    displayName: 'User Administration', 
    description: 'User CRUD operations', 
    moduleKey: 'user_management.users', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 176, 
    name: 'Roles', 
    displayName: 'Role Management', 
    description: 'Role and permission management', 
    moduleKey: 'user_management.roles', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 177, 
    name: 'Organizations', 
    displayName: 'Organization Management', 
    description: 'Organization settings', 
    moduleKey: 'user_management.organizations', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 178, 
    name: 'Purchase Orders', 
    displayName: 'Purchase Order Management', 
    description: 'Create and manage purchase orders', 
    moduleKey: 'purchase_management.orders', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 179, 
    name: 'Goods Receipt', 
    displayName: 'Goods Receipt Management', 
    description: 'Receive and validate incoming goods', 
    moduleKey: 'purchase_management.receipt', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 180, 
    name: 'Purchase Returns', 
    displayName: 'Purchase Return Management', 
    description: 'Handle returns to suppliers', 
    moduleKey: 'purchase_management.returns', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 181, 
    name: 'Purchase Reports', 
    displayName: 'Purchase Analytics', 
    description: 'Purchase performance and analytics', 
    moduleKey: 'purchase_management.reports', 
    isActive: true, 
    sortOrder: 4,
    category: 'detailed'
  },
  { 
    id: 182, 
    name: 'Sales Orders', 
    displayName: 'Sales Order Management', 
    description: 'Create and manage sales orders', 
    moduleKey: 'sales_management.orders', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 183, 
    name: 'Order Fulfillment', 
    displayName: 'Order Fulfillment', 
    description: 'Pick, pack, and ship orders', 
    moduleKey: 'sales_management.fulfillment', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 184, 
    name: 'Customer Management', 
    displayName: 'Customer Relations', 
    description: 'Manage customer information and relationships', 
    moduleKey: 'sales_management.customers', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 185, 
    name: 'Sales Returns', 
    displayName: 'Sales Return Management', 
    description: 'Handle customer returns and refunds', 
    moduleKey: 'sales_management.returns', 
    isActive: true, 
    sortOrder: 4,
    category: 'detailed'
  },
  { 
    id: 186, 
    name: 'Supplier Management', 
    displayName: 'Supplier Database', 
    description: 'Manage supplier information and relationships', 
    moduleKey: 'vendor_management.suppliers', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 187, 
    name: 'Vendor Contracts', 
    displayName: 'Contract Management', 
    description: 'Manage vendor contracts and agreements', 
    moduleKey: 'vendor_management.contracts', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 188, 
    name: 'Vendor Performance', 
    displayName: 'Performance Analytics', 
    description: 'Track and analyze vendor performance', 
    moduleKey: 'vendor_management.performance', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 189, 
    name: 'Stock Operations', 
    displayName: 'Stock Movement Operations', 
    description: 'Core stock in/out operations', 
    moduleKey: 'inventory_management.stock_ops', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 190, 
    name: 'Warehouse Management', 
    displayName: 'Warehouse Operations', 
    description: 'Warehouse and location management', 
    moduleKey: 'inventory_management.warehouses', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 191, 
    name: 'Stock Adjustments', 
    displayName: 'Inventory Adjustments', 
    description: 'Stock level adjustments and corrections', 
    moduleKey: 'inventory_management.adjustments', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 192, 
    name: 'Stock Transfer', 
    displayName: 'Inter-warehouse Transfers', 
    description: 'Transfer stock between warehouses', 
    moduleKey: 'inventory_management.transfers', 
    isActive: true, 
    sortOrder: 4,
    category: 'detailed'
  },
  { 
    id: 193, 
    name: 'Products', 
    displayName: 'Product Management', 
    description: 'Product CRUD operations', 
    moduleKey: 'catalog_management.products', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 194, 
    name: 'Categories', 
    displayName: 'Category Management', 
    description: 'Product categorization', 
    moduleKey: 'catalog_management.categories', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 195, 
    name: 'Brands', 
    displayName: 'Brand Management', 
    description: 'Brand operations', 
    moduleKey: 'catalog_management.brands', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 196, 
    name: 'Variants', 
    displayName: 'Product Variants', 
    description: 'Variant management', 
    moduleKey: 'catalog_management.variants', 
    isActive: true, 
    sortOrder: 4,
    category: 'detailed'
  },
  { 
    id: 197, 
    name: 'Tasks', 
    displayName: 'Task Operations', 
    description: 'Task CRUD and management', 
    moduleKey: 'task_management.tasks', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 198, 
    name: 'Categories', 
    displayName: 'Task Categories', 
    description: 'Task categorization', 
    moduleKey: 'task_management.categories', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 199, 
    name: 'Workflows', 
    displayName: 'Workflow Management', 
    description: 'Task workflow automation', 
    moduleKey: 'task_management.workflows', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 200, 
    name: 'Purchase Reports', 
    displayName: 'Purchase Analytics', 
    description: 'Purchase performance reports', 
    moduleKey: 'reporting.purchase', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 201, 
    name: 'Sales Reports', 
    displayName: 'Sales Analytics', 
    description: 'Sales performance reports', 
    moduleKey: 'reporting.sales', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  },
  { 
    id: 202, 
    name: 'Inventory Reports', 
    displayName: 'Inventory Analytics', 
    description: 'Stock and inventory reports', 
    moduleKey: 'reporting.inventory', 
    isActive: true, 
    sortOrder: 3,
    category: 'detailed'
  },
  { 
    id: 203, 
    name: 'User Reports', 
    displayName: 'User Analytics', 
    description: 'User activity reports', 
    moduleKey: 'reporting.users', 
    isActive: true, 
    sortOrder: 4,
    category: 'detailed'
  },
  { 
    id: 204, 
    name: 'Customer Portal', 
    displayName: 'Customer Portal', 
    description: 'Customer self-service', 
    moduleKey: 'customer_interfaces.portal', 
    isActive: true, 
    sortOrder: 1,
    category: 'detailed'
  },
  { 
    id: 205, 
    name: 'Order Management', 
    displayName: 'Customer Orders', 
    description: 'Customer order operations', 
    moduleKey: 'customer_interfaces.orders', 
    isActive: true, 
    sortOrder: 2,
    category: 'detailed'
  }
];

// Helper functions
export function getModuleByKey(moduleKey: string): ModuleDefinition | undefined {
  return MODULE_DEFINITIONS.find(module => module.moduleKey === moduleKey && module.isActive);
}

export function getModuleById(id: number): ModuleDefinition | undefined {
  return MODULE_DEFINITIONS.find(module => module.id === id && module.isActive);
}

export function getModulesByCategory(category: 'core' | 'service' | 'detailed'): ModuleDefinition[] {
  return MODULE_DEFINITIONS
    .filter(module => module.category === category && module.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getActiveModules(): ModuleDefinition[] {
  return MODULE_DEFINITIONS.filter(module => module.isActive);
}

export function isValidModuleKey(moduleKey: string): boolean {
  return getModuleByKey(moduleKey) !== undefined;
}
